import { CustomState } from "@bevco/Store/Reducers";
import {
    ApiHandlerDiscreteParameter,
    createHandlerChainRunner,
    HasOnError,
    HasOnSuccess,
    markSkipOnCompleteIfOnErrorIsSet,
    markSkipOnCompleteIfOnSuccessIsSet,
} from "@insite/client-framework/HandlerCreator";
import { request } from "@insite/client-framework/Services/ApiService";
import siteMessage from "@insite/client-framework/SiteMessage";
import { getCurrentShipToState } from "@insite/client-framework/Store/Data/ShipTos/ShipTosSelectors";
import translate from "@insite/client-framework/Translate";

const newAddressUrl = "/api/v1/customBillingShippingDataModal/PostShippingData";

type SubmitNewShippingAddressFormParameter = {
    additionalData: {
        customerSequence: string;
        customerNumber: string;
        customerName: string;
        stateProvinceBilling: string;
        zipPostalCodeBilling: string;
        loginEmailUser: string;
        lastNameUser: string;
        firstNameUser: string;
    };
} & HasOnSuccess &
    HasOnError;

type HandlerType = ApiHandlerDiscreteParameter<
    SubmitNewShippingAddressFormParameter,
    FormData,
    any,
    { finishChain?: boolean }
>;

export const validateFormFields: HandlerType = props => {
    const state = props.getState() as CustomState;
    const addressForm = state.customNewShippingAddressForm.address;

    const currentShipTo = getCurrentShipToState(state);
    const shipToSettingsValidation = currentShipTo.value?.validation;

    if (!addressForm.firstName && shipToSettingsValidation?.firstName?.isRequired) {
        props.dispatch({
            type: "Components/MyAccount/SetNewShippingAddressValidation",
            key: "firstName",
            value: siteMessage("Field_Required", "First Name"),
        } as any);
        props.finishChain = true;
    } else {
        props.dispatch({
            type: "Components/MyAccount/SetNewShippingAddressValidation",
            key: "firstName",
            value: null,
        } as any);
    }

    if (!addressForm.lastName && shipToSettingsValidation?.lastName?.isRequired) {
        props.dispatch({
            type: "Components/MyAccount/SetNewShippingAddressValidation",
            key: "lastName",
            value: siteMessage("Field_Required", "Last Name"),
        } as any);
        props.finishChain = true;
    } else {
        props.dispatch({
            type: "Components/MyAccount/SetNewShippingAddressValidation",
            key: "lastName",
            value: null,
        } as any);
    }

    if (!addressForm.companyName && shipToSettingsValidation?.companyName?.isRequired) {
        props.dispatch({
            type: "Components/MyAccount/SetNewShippingAddressValidation",
            key: "companyName",
            value: siteMessage("Field_Required", "Company Name"),
        } as any);
        props.finishChain = true;
    } else {
        props.dispatch({
            type: "Components/MyAccount/SetNewShippingAddressValidation",
            key: "companyName",
            value: null,
        } as any);
    }

    if (!addressForm.address1) {
        props.dispatch({
            type: "Components/MyAccount/SetNewShippingAddressValidation",
            key: "address1",
            value: siteMessage("Field_Required", "Address1"),
        } as any);
        props.finishChain = true;
    } else {
        props.dispatch({
            type: "Components/MyAccount/SetNewShippingAddressValidation",
            key: "address1",
            value: null,
        } as any);
    }

    if (!addressForm.city) {
        props.dispatch({
            type: "Components/MyAccount/SetNewShippingAddressValidation",
            key: "city",
            value: siteMessage("Field_Required", "City"),
        } as any);
        props.finishChain = true;
    } else {
        props.dispatch({
            type: "Components/MyAccount/SetNewShippingAddressValidation",
            key: "city",
            value: null,
        } as any);
    }

    if (!addressForm.state) {
        props.dispatch({
            type: "Components/MyAccount/SetNewShippingAddressValidation",
            key: "state",
            value: siteMessage("Field_Required", "State"),
        } as any);
        props.finishChain = true;
    } else {
        props.dispatch({
            type: "Components/MyAccount/SetNewShippingAddressValidation",
            key: "state",
            value: null,
        } as any);
    }

    if (!addressForm.postalCode || addressForm.postalCode.trim().length < 5) {
        props.dispatch({
            type: "Components/MyAccount/SetNewShippingAddressValidation",
            key: "postalCode",
            value: siteMessage("Field_Required", "Postal Code"),
        } as any);
        props.finishChain = true;
    } else {
        props.dispatch({
            type: "Components/MyAccount/SetNewShippingAddressValidation",
            key: "postalCode",
            value: null,
        } as any);
    }

    if (!addressForm.email || !/\w+([-+."]\w+)*@\w+([-.]\w+)*\.\w+([-.]\w+)*/g.test(addressForm.email)) {
        props.dispatch({
            type: "Components/MyAccount/SetNewShippingAddressValidation",
            key: "email",
            value: siteMessage("Field_Required", "Email"),
        } as any);
        props.finishChain = true;
    } else {
        props.dispatch({
            type: "Components/MyAccount/SetNewShippingAddressValidation",
            key: "email",
            value: null,
        } as any);
    }

    if (addressForm.phone && !/^([\(\)/\-\.\+\s]*\d\s?(ext)?[\(\)/\-\.\+\s]*){10,}$/g.test(addressForm.phone)) {
        props.dispatch({
            type: "Components/MyAccount/SetNewShippingAddressValidation",
            key: "phone",
            value: siteMessage("Field_Required", "Phone"),
        } as any);
        props.finishChain = true;
    } else {
        props.dispatch({
            type: "Components/MyAccount/SetNewShippingAddressValidation",
            key: "phone",
            value: null,
        } as any);
    }

    if (props.finishChain) {
        markSkipOnCompleteIfOnErrorIsSet(props);
        props.parameter.onError?.();
    }
};

export const PopulateApiParameter: HandlerType = (props: any) => {
    if (!props.finishChain) {
        const state = (props.getState() as any).customNewShippingAddressForm.address;
        props.apiParameter = {
            ...props.apiParameter,
            ...props.parameter.additionalData,
            address1: state.address1,
            address2: state.address2,
            city: state.city,
            companyName: state.companyName,
            country: state.country.name,
            state: state.state.name,
            email: state.email,
            phone: state.phone,
            postalCode: state.postalCode,
            firstName: state.firstName,
            lastName: state.lastName,
        };
    }
};

export function multipart<Parameter, Result = Parameter>(endpoint: string, formData: any) {
    return request<Result>(
        endpoint,
        "POST",
        { Accept: "application/json", "Content-Type": "application/json" },
        JSON.stringify(formData),
    );
}

export function submitCreateAccountFormApi(parameter: any) {
    return multipart<any, void>(`${newAddressUrl}`, parameter);
}

export const SendDataToApi: HandlerType = async props => {
    if (!props.finishChain) {
        await submitCreateAccountFormApi(props.apiParameter);
    }
};

export const ExecuteOnSuccessCallback: HandlerType = props => {
    if (!props.finishChain) {
        markSkipOnCompleteIfOnSuccessIsSet(props);
        props.parameter.onSuccess?.();
    }
};

export const chain = [validateFormFields, PopulateApiParameter, SendDataToApi, ExecuteOnSuccessCallback];

const submitNewShippingAddressForm = createHandlerChainRunner(chain, "SubmitNewAddressForm");
export default submitNewShippingAddressForm;
